import {tgxApi} from "../../app/tgxApi";
import {getAuthorizationHeader, handleQueryError, handleQueryErrorAndSuccess} from "../../helpers/utils/RtkQueryUtils";
import {serialize} from "../../helpers/utils/Utils";
import {fetchBaseQuery} from "@reduxjs/toolkit/query/react";

export const mfaDeviceApi = tgxApi.injectEndpoints({
    endpoints: (build) => ({

        createMfaDevice: build.mutation({
            query: (requestBody) => ({
                url: `/ajax/mfa-devices`,
                method: 'POST',
                headers: getAuthorizationHeader(),
                body: requestBody,
            }),
            invalidatesTags: ['mfaDevices'],
            async onQueryStarted(_, { queryFulfilled }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Mfa Device");
            },
        }),

        deleteMfaDeviceById: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/mfa-devices/${id}`,
                    method: 'DELETE',
                    headers: getAuthorizationHeader()
                };
            },
            invalidatesTags: ['mfaDevices'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "Mfa Device");
            },
        }),


        solveMfaDeviceChallenge: build.mutation({
            query: ({ challengeId, solution}) => {
                const paramData = serialize({solution: solution});
                return {
                    url: `/ajax/mfa-devices/${challengeId}/verify?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['mfaDevices'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Verified", "Mfa Device");
            },
        }),

        issueVerificationToken: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/mfa-devices/${id}/verify`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['mfaDevices'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled);
            },
        }),

        preferMfaDeviceById: build.mutation({
            query: (id) => {
                return {
                    url: `/ajax/mfa-devices/${id}/prefer`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['mfaDevices'],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Preferred", "Mfa Device");
            },
        }),

        getUserMfaDevice: build.query({
            query: (userId) => {

                return {
                    url: `/ajax/mfa-devices/${userId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ["mfaDevices"],
            async onQueryStarted(_, {queryFulfilled,}) {
                await handleQueryError(queryFulfilled)
            },
        }),

        generateQrCode: build.query<string, string>({
            async queryFn(id, api, _extraOptions, fetchWithBQ) {
                const rawBaseQuery = fetchBaseQuery({
                    baseUrl: process.env.REACT_APP_BASE_URL,
                    responseHandler: 'text',
                    prepareHeaders: (headers) => {
                        headers.set('Authorization', getAuthorizationHeader().Authorization);
                        return headers;
                    },
                });
                const result = await rawBaseQuery(
                    { url: `/ajax/mfa-devices/${id}/qr-code`, method: 'GET' },
                    api,
                    {}
                );
                if (result.error) return { error: result.error };
                return { data: result.data as string };
            },
        }),

        verifyMfaDevice: build.mutation({
            query: ({deviceId, mfaToken}) => {
                const paramData = serialize({ mfaToken: mfaToken });
                return {
                    url: `/ajax/mfa-devices/${deviceId}/verify?${paramData}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['mfaDevices'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled);
            },
        }),

        requestMfaChallenge: build.mutation({
            query: ({ challengeId, mfaDeviceId, remember }) => {
                const paramData = serialize({ challengeId: challengeId, mfaDeviceId: mfaDeviceId, remember: remember });
                return {
                    url: `/authenticate/mfa/challenge?${paramData}`,
                    method: 'POST',
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled);
            },
        }),

        verifyMfaChallenge: build.mutation({
            query: ({ challengeId, solution }) => {
                const paramData = serialize({ challengeId: challengeId, solution: solution });
                return {
                    url: `/authenticate/mfa/verify?${paramData}`,
                    method: 'POST'
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled);
            },
        }),
    })
});

export const {
    useCreateMfaDeviceMutation,
    useDeleteMfaDeviceByIdMutation,
    useSolveMfaDeviceChallengeMutation,
    usePreferMfaDeviceByIdMutation,
    useGetUserMfaDeviceQuery,
    useIssueVerificationTokenMutation,
    useLazyGenerateQrCodeQuery,
    useGenerateQrCodeQuery,
    useRequestMfaChallengeMutation,
    useVerifyMfaChallengeMutation,
    useVerifyMfaDeviceMutation
} = mfaDeviceApi